/**
 * ClinicTicketDetailPage — one CLINIC ticket with its full message thread.
 *
 * Route: `/clinic-tickets/:ticketId` (ProtectedRoute).
 *
 * Mirrors TicketDetailPage (platform support) but for the customer ↔ clinic
 * stream: the non-user author is the CLINIC ('clinic'), labelled accordingly,
 * and the header shows which clinic the ticket is addressed to. Messages
 * render as start/end chat bubbles based on author (user on the end side,
 * clinic on the start side — auto-mirrored under RTL). Reply box posts a new
 * message; hidden with a notice when the ticket is `closed`.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Skeleton,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';

import LanguageToggle from '@/components/LanguageToggle';
import { clinicTicketsApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatDate } from '@/utils/date';
import { localized } from '@/utils/localizedContent';

function statusChipColor(status) {
  switch (status) {
    case 'open':        return 'warning';
    case 'in_progress': return 'info';
    case 'resolved':    return 'success';
    case 'closed':      return 'default';
    default:            return 'default';
  }
}

/**
 * One message bubble. `isUser` (author 'user') renders on the end side in
 * primary color; the clinic ('clinic', or any non-user author) renders on
 * the start side in grey with a storefront avatar.
 */
function MessageBubble({ message, language, t }) {
  const isUser = message.author === 'user';
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-end"
      sx={{ justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      {!isUser && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            flexShrink: 0,
          }}
        >
          <StorefrontIcon fontSize="small" />
        </Box>
      )}
      <Box
        sx={{
          maxWidth: '78%',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          borderTopRightRadius: isUser ? 4 : 16,
          borderTopLeftRadius: isUser ? 16 : 4,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ opacity: 0.85, display: 'block', mb: 0.25, fontWeight: 600 }}
        >
          {isUser ? t('clinicSupport.detail.you') : t('clinicSupport.detail.clinic')}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.body}
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, display: 'block', mt: 0.5, fontSize: '0.7rem' }}
        >
          {formatDate(message.createdAt, language, { style: 'datetime' })}
        </Typography>
      </Box>
      {isUser && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'grey.300',
            color: 'text.primary',
            flexShrink: 0,
          }}
        >
          <PersonIcon fontSize="small" />
        </Box>
      )}
    </Stack>
  );
}

export function ClinicTicketDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);
  const queryClient = useQueryClient();

  const [reply, setReply] = useState('');
  const [replyError, setReplyError] = useState(null);
  const bottomRef = useRef(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['clinicTickets', ticketId],
    queryFn: () => clinicTicketsApi.getTicket(ticketId),
    staleTime: 15 * 1000,
  });

  const ticket = data?.data?.ticket;
  const clinicLabel = localized(ticket?.clinicName, language) || ticket?.clinicCode || '';

  const replyMutation = useMutation({
    mutationFn: () => clinicTicketsApi.addMessage(ticketId, reply),
    onSuccess: ({ data: payload }) => {
      queryClient.setQueryData(['clinicTickets', ticketId], { data: payload });
      queryClient.invalidateQueries({ queryKey: ['clinicTickets'] });
      setReply('');
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'TICKET_CLOSED') {
        setReplyError(t('clinicSupport.detail.ticketClosed'));
      } else if (code === 'VALIDATION_ERROR') {
        setReplyError(t('clinicSupport.detail.replyRequired'));
      } else {
        setReplyError(t('clinicSupport.error.generic'));
      }
    },
  });

  // Scroll to the latest message when the thread updates.
  useEffect(() => {
    if (ticket?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [ticket?.messages?.length]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/clinic-tickets');
  };

  const handleSendReply = () => {
    setReplyError(null);
    if (!reply.trim()) {
      setReplyError(t('clinicSupport.detail.replyRequired'));
      return;
    }
    replyMutation.mutate();
  };

  const canReply = ticket && ticket.status !== 'closed';

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton size="small" onClick={handleBack} aria-label={t('common.back')}>
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <Typography variant="h6" component="h1" fontWeight={700} noWrap>
              {t('clinicSupport.detail.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Loading */}
        {isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="text" width="70%" height={32} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('clinicSupport.error.loadFailed')}
          </Alert>
        )}

        {/* Ticket header card */}
        {!isLoading && !isError && ticket && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {ticket.subject}
              </Typography>
              {clinicLabel && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mb: 1 }}>
                  <StorefrontIcon fontSize="small" />
                  <Typography variant="body2">{clinicLabel}</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  color={statusChipColor(ticket.status)}
                  label={t(`clinicSupport.status.${ticket.status}`, {
                    defaultValue: ticket.status,
                  })}
                  variant="filled"
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`clinicSupport.category.${ticket.category}`, {
                    defaultValue: ticket.category,
                  })}
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        {!isLoading && !isError && ticket?.messages && (
          <Stack spacing={2}>
            {(ticket.messages || []).map((msg) => (
              <MessageBubble key={msg.id} message={msg} language={language} t={t} />
            ))}
            <Box ref={bottomRef} />
          </Stack>
        )}

        {/* "Awaiting reply" hint when only the user has spoken so far */}
        {!isLoading && !isError && ticket?.status === 'open' && (
          <Alert severity="info" variant="outlined">
            {t('clinicSupport.detail.awaitingReply')}
          </Alert>
        )}

        {/* Reply box — only when ticket is not closed */}
        {canReply && (
          <Stack spacing={1.5}>
            <TextField
              label={t('clinicSupport.detail.replyPlaceholder')}
              value={reply}
              onChange={(e) => {
                setReply(e.target.value);
                if (replyError) setReplyError(null);
              }}
              disabled={replyMutation.isPending}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 2000 }}
              error={!!replyError}
              helperText={replyError ?? ' '}
            />
            <Button
              variant="contained"
              onClick={handleSendReply}
              disabled={replyMutation.isPending}
              startIcon={
                replyMutation.isPending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {replyMutation.isPending
                ? t('clinicSupport.detail.sending')
                : t('clinicSupport.detail.send')}
            </Button>
          </Stack>
        )}

        {/* Closed-ticket notice */}
        {!isLoading && !isError && ticket?.status === 'closed' && (
          <Alert severity="warning">{t('clinicSupport.detail.ticketClosed')}</Alert>
        )}
      </Stack>
    </Container>
  );
}

export default ClinicTicketDetailPage;
